/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerAccountLinking */
/* eslint-disable import/extensions */
import Auth0UtilsBrowser from '../../../../utils/auth0-utils-browser.mjs';
import Auth0PostLoginHandlerBase from '../auth0-post-login-handler-base.mst.mjs';
import Auth0UserSessionHelper from '../../../../utils/auth0-session-helper.mjs';
import Auth0Constants from '../../../../utils/auth0-constants.mjs';
import { Auth0LinkingPageHelper } from '../../../pages/auth0-sso-link-page-helper.mst.mjs';
import Auth0IdentityProvider from '../../../../utils/auth0-identity-provider.mjs';
import Auth0CookieHelper from '../../../../utils/auth0-cookie-helper.mjs';
import Auth0SupportedIdps from '../../../../utils/auth0-supported-idps.mjs';
import logger from '../../../../utils/logging/auth0-logger.mjs';
import Auth0PostLoginHandlerNewSsoUser from './auth0-post-login-handler-new-sso-user.mjs';
import { PROVIDER_AUTH0 } from '../../auth-adapters/constants.mjs';
import Auth0TokenStorageHelper from '../auth0-token-storage-helper.mjs';

const LINKING_TOKEN_KEY = Auth0Constants.AUTH0_LINKING_TOKEN_KEY;

class Auth0PostLoginHandlerAccountLinking extends Auth0PostLoginHandlerBase {
  constructor(options) {
    super(options);
    this.socialIdpHelper = new Auth0SupportedIdps({
      tenantOrDomain: this._ssOptions.tenant,
      appLoginHandlerUrl: this._ssOptions.redirectAuth0Uri,
      activeIdps: options.activeIdps,
    });
    this.supportedSocialIdps = this.socialIdpHelper.supportedIdps;

    this._auth0 = window.auth0SpaJs || window.auth0;
    if (!this._auth0 && options.providerType === PROVIDER_AUTH0) {
      throw new Error('Unable to initialize Auth0PostLoginHandlerAccountLinking because it cannot find a valid Auth0 package');
    }
    this._initLogger('Auth0PostLoginHandlerAccountLinking');
  }

  _shouldHandleSync = (loginData) => {
    console.log('_shouldHandleSync => checking for admin-initiated linking flow...');
    console.log('_shouldHandleSync => appState:', JSON.stringify(loginData?.appState));

    // FIRST: Check if this is a user-initiated linking COMPLETION flow (redirect-based)
    // This means user just completed SS auth after clicking "Connect" on linking page
    if (this._isUserInitiatedLinkingCompletion(loginData)) {
      console.log('_shouldHandleSync => user-initiated linking completion detected (redirect flow)');
      return true;
    }

    // SECOND: Check if this is an admin-initiated linking flow with a token in appState
    if (this._hasPendingLinkingToken(loginData)) {
      // But if user is already linked to a SS account, skip this handler entirely.
      // The token was likely already consumed on a previous auth callback, and
      // we should let the normal SSHome handler process this authenticated user.
      if (this._isIdpUserLinkedToSsAccount(loginData)) {
        console.log('Admin-initiated linking flow detected but user already linked to SS account, skipping handler');
        return false;
      }
      console.log('Admin-initiated linking flow detected with pending token in appState');
      return true;
    }

    console.log('_shouldHandleSync => no pending linking token, checking for user-initiated linking...');

    // handles if:
    // - The currently logged in user is NOT linked to a SS account
    // - The current browser state does NOT indicate a new user onboarding is in progress
    if (this._isIdpUserLinkedToSsAccount(loginData)) {
      console.log('_shouldHandleSync => user is already linked to SS account, skip linking');
      return false;
    }

    // don't handle ssas requests as we're still in active development and not yet able to do
    // any linking
    if (this._providerType !== PROVIDER_AUTH0) {
      console.log('_shouldHandleSync => SSAS provider, skip linking (not yet supported)');
      return false;
    }

    // Check if this is a new user onboarding flow
    if (Auth0PostLoginHandlerNewSsoUser.isIdpUserOnboardingRedirect(loginData)) {
      console.log('_shouldHandleSync => new user onboarding flow, skip linking');
      return false;
    }

    // ONLY show linking UI if user explicitly clicked an SSO button (user-initiated linking)
    // Check both direct appState and nested additionalState for the flag
    const isUserInitiatedLinking = this._isUserInitiatedLinking(loginData);
    if (!isUserInitiatedLinking) {
      console.log('_shouldHandleSync => not user-initiated linking (no flag in appState), skip linking');
      return false;
    }

    console.log('_shouldHandleSync => user-initiated linking flow detected, showing linking UI');
    return true;
  };

  /**
   * Check if this is the COMPLETION phase of a user-initiated linking flow (redirect-based)
   * This is set when user clicks "Connect" on linking page, caches SSO JWT, and redirects to SS auth.
   * After SS auth completes, we detect this flag and complete the linking via SSAS.
   * @param {object} loginData - Login data returned from OAuth callback
   * @returns {boolean} True if this is the completion phase of redirect-based linking
   */
  _isUserInitiatedLinkingCompletion = (loginData) => {
    try {
      // Check direct flag in appState (set by _loginToShipStationWithRedirect)
      if (loginData?.appState?.userInitiatedLinkingCompletion) {
        console.log('_isUserInitiatedLinkingCompletion => found flag in appState.userInitiatedLinkingCompletion');
        return true;
      }

      // Check for flag in additionalState (in case it gets nested)
      const additionalState = loginData?.appState?.additionalState;
      if (additionalState) {
        const parsedState = typeof additionalState === 'string'
          ? JSON.parse(additionalState)
          : additionalState;

        if (parsedState?.userInitiatedLinkingCompletion) {
          console.log('_isUserInitiatedLinkingCompletion => found flag in appState.additionalState');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to check for user-initiated linking completion flag:', error);
      return false;
    }
  };

  /**
   * Check if this is a user-initiated linking flow (user clicked SSO button)
   * @param {object} loginData - Login data returned from OAuth callback
   * @returns {boolean} True if user explicitly initiated linking
   */
  _isUserInitiatedLinking = (loginData) => {
    try {
      // Check direct flag in appState
      if (loginData?.appState?.userInitiatedLinking) {
        console.log('_isUserInitiatedLinking => found flag in appState.userInitiatedLinking');
        return true;
      }

      // Check for flag in additionalState (comes from IdP signinWithRedirectHandler)
      const additionalState = loginData?.appState?.additionalState;
      if (additionalState) {
        const parsedState = typeof additionalState === 'string'
          ? JSON.parse(additionalState)
          : additionalState;

        if (parsedState?.userInitiatedLinking) {
          console.log('_isUserInitiatedLinking => found flag in appState.additionalState');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to check for user-initiated linking flag:', error);
      return false;
    }
  };

  /**
   * @inheritdoc
   */
  async shouldHandle(loginData) {
    return Promise.resolve(this._shouldHandleSync(loginData));
  }

  /**
   * @inheritdoc
   */
  async run(loginData) {
    console.log('run() => checking for linking flow type...');
    console.log('run() => appState:', JSON.stringify(loginData?.appState));

    // Store tokens in the default locations (legacy _lat and _latv3 cookies)
    if (loginData.tokens) {
      Auth0TokenStorageHelper.storeTokensDefault(loginData.tokens);
    }

    // FIRST: Check if this is a user-initiated linking COMPLETION flow (redirect-based)
    // This is when user just completed SS auth after clicking "Connect" on linking page
    if (this._isUserInitiatedLinkingCompletion(loginData)) {
      console.log('User-initiated linking completion flow: completing linking via SSAS...');
      await this._handleUserInitiatedLinkingCompletion(loginData);
      return;
    }

    // SECOND: Check if this is an admin-initiated linking flow (token in appState from OAuth redirect)
    const pendingToken = this._retrieveStoredLinkingToken(loginData);

    console.log('run() => retrieved pendingToken:', pendingToken);

    if (pendingToken) {
      console.log('Admin-initiated linking flow: consuming token from appState...');
      await this._handleAdminLinkingFlow(loginData, pendingToken);
      return;
    }

    // Otherwise, handle normal user-initiated linking (show linking UI)
    console.log(`_handleStartLinkToSsoUser => unlinked user ${loginData?.user?.sub} is requesting account linking...`);
    const linkingData = await this._getIdpTokenData(Auth0IdentityProvider.getIdpIdFromUserId(loginData.user.sub));
    this._renderShipStationLinkingConfirmation(loginData, linkingData);
  }

  // ============================================================================
  // User-initiated linking completion (redirect-based flow)
  // ============================================================================

  /**
   * Handle completion of user-initiated linking after SS auth redirect.
   *
   * Flow:
   * 1. User was on linking page (authenticated as SSO user)
   * 2. User clicked "Connect" → SSO JWT cached in sessionStorage → redirected to SS auth
   * 3. User completed SS auth → now we have SS user JWT in loginData.tokens
   * 4. This method: retrieves cached SSO JWT, calls SSAS /user/link with BOTH JWTs
   * 5. After successful linking, redirect directly to target URL (no re-login needed
   *    because user already has valid SS JWT from the redirect auth)
   *
   * @param {object} loginData - Login data from SS auth callback
   */
  _handleUserInitiatedLinkingCompletion = async (loginData) => {
    try {
      console.log('_handleUserInitiatedLinkingCompletion => starting...');

      // Step 1: Retrieve cached SSO credentials from sessionStorage
      const cachedCredentials = Auth0LinkingPageHelper.getCachedSsoCredentials();

      if (!cachedCredentials || !cachedCredentials.ssoUserJwt || !cachedCredentials.ssoUserId) {
        console.error('_handleUserInitiatedLinkingCompletion => cached SSO credentials missing or incomplete');
        Auth0UtilsBrowser.setAuth0IsError('linking-session-expired');
        this._logoutAndRedirectToLogin();
        return new Promise(() => {}); // Never resolves - browser navigates away
      }

      const {
        ssoUserJwt, ssoUserId, idpId, redirectUrl,
      } = cachedCredentials;

      console.log('_handleUserInitiatedLinkingCompletion => retrieved cached SSO credentials:', {
        ssoUserId,
        idpId,
        hasJwt: !!ssoUserJwt,
        redirectUrl,
        hasTokenStorageConfig: !!cachedCredentials.tokenStorageConfig,
        tokenStorageConfig: cachedCredentials.tokenStorageConfig ?? null,
      });

      // Step 2: Get SS user's JWT from login callback (this is the PRIMARY user JWT)
      // IMPORTANT: Use the ID token, NOT the access token!
      // The access token may be cached from a previous session (e.g., the SSO session),
      // but the ID token is always fresh from the just-completed authentication.
      // After redirect-based SS auth with Central-Connection, loginData.tokens.idToken
      // contains the SS user's identity (auth0|...), while loginData.tokens.accessToken
      // might be a cached token from the previous SSO session.
      const ssUserJwt = loginData.tokens?.idToken || loginData.tokens?.accessToken;

      // DEBUG: Log what we received from Auth0 callback
      console.log('_handleUserInitiatedLinkingCompletion => loginData.user:', loginData.user);
      console.log('_handleUserInitiatedLinkingCompletion => loginData.user.sub:', loginData.user?.sub);
      console.log('_handleUserInitiatedLinkingCompletion => ssUserJwt sub:', ssUserJwt ? Auth0UtilsBrowser.getJwtBodyFromToken(ssUserJwt).sub : 'no jwt');
      console.log('_handleUserInitiatedLinkingCompletion => using idToken:', !!loginData.tokens?.idToken);
      console.log('_handleUserInitiatedLinkingCompletion => accessToken sub:', loginData.tokens?.accessToken ? Auth0UtilsBrowser.getJwtBodyFromToken(loginData.tokens.accessToken).sub : 'no token');

      if (!ssUserJwt) {
        console.error('_handleUserInitiatedLinkingCompletion => SS user JWT not available in loginData.tokens');
        Auth0UtilsBrowser.setAuth0IsError('linking-auth-failed');
        this._logoutAndRedirectToLogin();
        return new Promise(() => {});
      }

      // Validate that the SS user is different from the SSO user
      const ssUserSub = Auth0UtilsBrowser.getJwtBodyFromToken(ssUserJwt).sub;
      if (ssUserSub === ssoUserId) {
        console.error('_handleUserInitiatedLinkingCompletion => SS user is same as SSO user - did you login with SSO instead of username/password?');
        console.error('Expected: auth0|... user, Got:', ssUserSub);
        Auth0UtilsBrowser.setAuth0IsError('linking-same-user');
        this._logoutAndRedirectToLogin();
        return new Promise(() => {});
      }

      // Step 3: Call SSAS /user/link with BOTH JWTs for dual validation
      // - primaryUserJwt: SS user JWT (ID token) - proves ownership of SS account
      // - secondaryUserJwt: SSO user JWT (ID token) - proves ownership of SSO identity
      // The primary JWT goes in Authorization header (aud claim has clientId for tenant resolution).
      // SSAS validates BOTH JWTs in the body for actual linking authorization.
      console.log('_handleUserInitiatedLinkingCompletion => calling SSAS to link accounts...');

      const linkResult = await this._auth0ManagementApi.linkUsers({
        primaryUserJwt: ssUserJwt,
        secondaryUserJwt: ssoUserJwt,
      });

      console.log('_handleUserInitiatedLinkingCompletion => linking result:', linkResult);

      // Step 4: Clear cached SSO credentials (one-time use)
      Auth0LinkingPageHelper.clearCachedSsoCredentials();

      // Step 5: Write linking result cookie for consuming app (if it set up a request cookie)
      this._writeLinkingResultCookie({
        success: true,
        idpName: this.socialIdpHelper?.getIdpNameById?.(idpId) || idpId,
        idpId,
        idpUsername: cachedCredentials.email,
        ssUsername: loginData.user?.email,
        message: 'Account linked successfully',
      });

      console.log('_handleUserInitiatedLinkingCompletion => accounts linked successfully');

      // Step 6: Trigger re-login with the newly linked SSO identity
      // This ensures:
      // 1. User gets fresh tokens that include the linked identity
      // 2. Proper authentication flow with tenant context
      // 3. Consistent behavior with admin-initiated linking flow
      //
      // The appLoginHandlerUrl should include targetClientId for tenant resolution.
      // If not present, add it to ensure the re-login flow works correctly.
      console.log('[appLoginUrl custody] _handleUserInitiatedLinkingCompletion => cachedCredentials.appLoginHandlerUrl:', cachedCredentials.appLoginHandlerUrl);
      console.log('[appLoginUrl custody] _handleUserInitiatedLinkingCompletion => ssOptions.appLoginUrl (not used here — should match cached):', this._ssOptions?.appLoginUrl);
      let appLoginUrl = cachedCredentials.appLoginHandlerUrl || `${window.location.origin}/loginauth`;
      if (this._ssOptions.clientId && !appLoginUrl.includes('targetClientId')) {
        const separator = appLoginUrl.includes('?') ? '&' : '?';
        appLoginUrl = `${appLoginUrl}${separator}targetClientId=${encodeURIComponent(this._ssOptions.clientId)}`;
      }

      console.log('[WL:LinkCompletion] this._whiteLabelOptions =>', JSON.stringify(this._whiteLabelOptions));
      console.log('[WL:LinkCompletion] cachedCredentials.appLoginHandlerUrl =>', cachedCredentials.appLoginHandlerUrl);
      console.log('[WL:LinkCompletion] appLoginUrl before wl inject =>', appLoginUrl);
      console.log('[WL:LinkCompletion] current page URL =>', window.location.href);
      console.log('[WL:LinkCompletion] authOptionsToken in qs =>', Auth0UtilsBrowser.parseQueryString().get('authOptionsToken') || '(not found)');

      const finalRedirectUrl = redirectUrl || loginData.appState?.redirectOverride || loginData.appState?.targetUrl || null;
      console.log('_handleUserInitiatedLinkingCompletion => redirect resolution:', {
        'cachedCredentials.redirectUrl': redirectUrl,
        'appState.redirectOverride': loginData.appState?.redirectOverride,
        'appState.targetUrl': loginData.appState?.targetUrl,
        finalRedirectUrl,
        idpId,
        appLoginUrl,
      });

      // Use shared utility to trigger re-login (same as admin-initiated linking flow)
      Auth0UtilsBrowser.triggerReLoginWithLinkedAccount(idpId, appLoginUrl, finalRedirectUrl);

      // Return promise that never resolves to prevent further code execution
      return new Promise(() => {});
    } catch (error) {
      console.error('_handleUserInitiatedLinkingCompletion => failed:', error);

      // Clear cached credentials on error
      Auth0LinkingPageHelper.clearCachedSsoCredentials();

      // Extract error details for display
      const serverErrorCode = error.response?.dataObj?.errorCode;
      const serverMessage = error.response?.dataObj?.message;
      const errorForDisplay = serverErrorCode || serverMessage || error.message || 'linking-failed';

      console.warn('_handleUserInitiatedLinkingCompletion => error details:', {
        errorCode: serverErrorCode,
        serverMessage,
        errorForDisplay,
      });

      // Write failure result cookie
      this._writeLinkingResultCookie({
        success: false,
        message: errorForDisplay,
      });

      // Set error and redirect to login page
      Auth0UtilsBrowser.setAuth0IsError(errorForDisplay);
      this._logoutAndRedirectToLogin();

      return new Promise(() => {});
    }
  };

  /**
   * Write linking result to cookie for consuming app to read
   * @param {object} result - Linking result object
   */
  _writeLinkingResultCookie = (result) => {
    // An app that requested linking may emit this cookie before starting the link flow.
    // If present, we will update it with linking status.
    if (!Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_LINKING_REQUEST_STATE)) {
      console.log('Account linker did not find a request for linking state cookie...');
      return;
    }
    Auth0CookieHelper.setCookieValueWithExpirationInSeconds(
      Auth0Constants.AUTH0_LINKING_REQUEST_STATE,
      JSON.stringify(result),
      180,
    );
  };

  // ============================================================================
  // Admin-initiated linking with tokens (via OAuth appState)
  // ============================================================================

  /**
   * Check if loginData contains a linking token in appState
   * @param {object} loginData - Login data returned from OAuth callback
   * @returns {boolean} True if linking token present in appState
   */
  _hasPendingLinkingToken = (loginData) => {
    try {
      console.log(`_hasPendingLinkingToken => checking appState.${LINKING_TOKEN_KEY}:`, loginData?.appState?.[LINKING_TOKEN_KEY]);

      // Check direct linkingToken in appState
      if (loginData?.appState?.[LINKING_TOKEN_KEY]) {
        console.log(`_hasPendingLinkingToken => found direct ${LINKING_TOKEN_KEY}`);
        return true;
      }

      // Check for linkingToken in additionalState (comes from IdP signinWithRedirectHandler)
      const additionalState = loginData?.appState?.additionalState;
      console.log('_hasPendingLinkingToken => checking additionalState:', additionalState);

      if (additionalState) {
        // additionalState may be a string that needs parsing
        const parsedState = typeof additionalState === 'string'
          ? JSON.parse(additionalState)
          : additionalState;

        console.log('_hasPendingLinkingToken => parsed additionalState:', parsedState);
        console.log(`_hasPendingLinkingToken => ${LINKING_TOKEN_KEY} in parsed state:`, parsedState?.[LINKING_TOKEN_KEY]);

        if (parsedState?.[LINKING_TOKEN_KEY]) {
          console.log(`_hasPendingLinkingToken => found ${LINKING_TOKEN_KEY} in additionalState`);
          return true;
        }
      }

      console.log('_hasPendingLinkingToken => no linking token found');
      return false;
    } catch (error) {
      console.error('Failed to check for pending linking token in appState:', error);
      return false;
    }
  };

  /**
   * Retrieve linking token from OAuth appState
   * @param {object} loginData - Login data returned from OAuth callback
   * @returns {string|null} Linking token or null if not present
   */
  _retrieveStoredLinkingToken = (loginData) => {
    try {
      // Check direct linkingToken in appState
      const directToken = loginData?.appState?.[LINKING_TOKEN_KEY];
      if (directToken) {
        console.log(`Linking token retrieved from appState.${LINKING_TOKEN_KEY}`);
        return directToken;
      }

      // Check for linkingToken in additionalState (comes from IdP signinWithRedirectHandler)
      const additionalState = loginData?.appState?.additionalState;
      if (additionalState) {
        // additionalState may be a string that needs parsing
        const parsedState = typeof additionalState === 'string'
          ? JSON.parse(additionalState)
          : additionalState;

        const tokenFromAdditionalState = parsedState?.[LINKING_TOKEN_KEY];
        if (tokenFromAdditionalState) {
          console.log('Linking token retrieved from appState.additionalState');
          return tokenFromAdditionalState;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to retrieve linking token from appState:', error);
      return null;
    }
  };

  /**
   * Strip linkingToken from URL to prevent infinite loop after successful linking
   * Handles both query parameter linkingToken and linkingToken embedded in additionalState
   * @param {string|null} url - URL to clean
   * @returns {string|null} Cleaned URL or null if input was null/empty
   */
  _stripLinkingTokenFromUrl = (url) => {
    if (!url) {
      return null;
    }

    // Strip direct linkingToken query parameter using utility
    let cleanedUrl = Auth0UtilsBrowser.stripSearchParams(url, LINKING_TOKEN_KEY, window.location.origin);

    // Also clean linkingToken from additionalState if present
    try {
      const urlObj = new URL(cleanedUrl, window.location.origin);
      const additionalState = urlObj.searchParams.get('additionalState');

      if (additionalState) {
        const decoded = decodeURIComponent(additionalState);
        const parsed = JSON.parse(decoded);

        if (parsed[LINKING_TOKEN_KEY]) {
          delete parsed[LINKING_TOKEN_KEY];

          // If additionalState is now empty, remove it entirely
          if (Object.keys(parsed).length > 0) {
            urlObj.searchParams.set('additionalState', encodeURIComponent(JSON.stringify(parsed)));
          } else {
            urlObj.searchParams.delete('additionalState');
          }
          cleanedUrl = urlObj.toString();
        }
      }
    } catch (parseError) {
      console.warn(`Failed to parse additionalState when stripping ${LINKING_TOKEN_KEY}:`, parseError);
      // Leave additionalState as-is if we can't parse it
    }

    console.log(`Stripped ${LINKING_TOKEN_KEY} from URL:`, { original: url, cleaned: cleanedUrl });
    return cleanedUrl;
  };

  _consumeLinkingToken = async (token, ssoUserId, ssoEmail) => {
    try {
      console.log('Consuming linking token via management API...');

      // Use the management API class following the established pattern
      const result = await this._auth0ManagementApi.consumeLinkingToken({
        token,
        ssoUserId,
        ssoEmail,
      });

      console.log('Linking token consumed successfully:', result);
      return result;
    } catch (error) {
      console.error('Error consuming linking token:', error);
      throw error;
    }
  };

  _handleAdminLinkingFlow = async (loginData, token) => {
    try {
      // Extract SSO identity info
      const ssoUserId = loginData.user.sub; // e.g., "google-oauth2|123456789"
      const ssoEmail = loginData.user.email;

      console.log('Consuming linking token for user:', { ssoUserId, ssoEmail, token });

      // Consume the token (one-time use via SSAS endpoint)
      const result = await this._consumeLinkingToken(token, ssoUserId, ssoEmail);

      if (result.alreadyLinked) {
        // Accounts already linked - user is authenticated and linked, just proceed with login.
        // No need for re-authentication - let the normal post-login flow continue.
        console.log('Accounts already linked, proceeding with normal login flow:', result);
        return; // Let normal flow continue
      }

      console.log('Account linking completed successfully:', result);

      // After successful NEW linking, trigger a re-login using the newly linked account
      // Extract IdP ID (connection name) from the SSO user ID
      // - Social OAuth: "google-oauth2|123..." -> "google-oauth2" (first segment)
      // - Enterprise OIDC: "oidc|woot-oauth2|plopech" -> "woot-oauth2" (second segment)
      // - Enterprise SAML: "samlp|okta|user123" -> "okta" (second segment)
      const idpId = Auth0IdentityProvider.getIdpIdFromUserId(ssoUserId);
      let appLoginHandlerUrl = this._ssOptions.appLoginUrl || this._ssOptions.redirectAuth0Uri || loginData.appState?.targetUrl || window.location.origin;
      console.log('[appLoginUrl custody] _handleAdminLinkingFlow => ssOptions.appLoginUrl:', this._ssOptions?.appLoginUrl);
      console.log('[appLoginUrl custody] _handleAdminLinkingFlow => ssOptions.redirectAuth0Uri (fallback):', this._ssOptions?.redirectAuth0Uri);
      console.log('[appLoginUrl custody] _handleAdminLinkingFlow => resolved appLoginHandlerUrl:', appLoginHandlerUrl);

      // Forward authOptionsToken so the re-login page gets white label branding
      const authOptionsToken = Auth0UtilsBrowser.parseQueryString().get('authOptionsToken');
      if (authOptionsToken) {
        try {
          const url = new URL(appLoginHandlerUrl);
          url.searchParams.set('authOptionsToken', authOptionsToken);
          appLoginHandlerUrl = url.toString();
        } catch {
          // malformed URL — proceed without token
        }
      }

      // Strip linkingToken from redirect URL to prevent infinite loop
      // The linkingToken was used to complete linking and should not persist in subsequent redirects
      const redirectUrl = this._stripLinkingTokenFromUrl(loginData.appState?.targetUrl);

      // Use shared utility to trigger re-login (same as user-initiated linking flow)
      Auth0UtilsBrowser.triggerReLoginWithLinkedAccount(idpId, appLoginHandlerUrl, redirectUrl);

      // Return a promise that never resolves to prevent any further code execution
      // while the browser navigates away
      return new Promise(() => {});
    } catch (error) {
      console.error('Failed to consume linking token:', error);

      // Extract error code and message from server response if available
      // The HTTP helper stores server JSON response in error.response.dataObj
      // SSAS returns { message: '...', errorCode: '...' } for linking errors
      const serverErrorCode = error.response?.dataObj?.errorCode;
      const serverMessage = error.response?.dataObj?.message;

      // Use error code for i18n lookup if available, otherwise fall back to message
      // The error code (e.g., 'sso-email-mismatch') maps to a key in messages.js
      // which Auth0Localizer will look up when displaying the error
      const errorForDisplay = serverErrorCode || serverMessage || error.message;

      console.warn('Account linking error for user:', {
        errorCode: serverErrorCode,
        serverMessage,
        errorForDisplay,
      });

      // Set error in cookie for display on login page
      // The login page will look up the localized message for the error code
      Auth0UtilsBrowser.setAuth0IsError(errorForDisplay);

      // Logout and redirect to clean login page
      // This clears the SSO session and prevents auto-login loop
      this._logoutAndRedirectToLogin();

      // Return a promise that never resolves to prevent any further code execution
      // while the browser navigates away
      return new Promise(() => {});
    }
  };

  /**
   * Logout the SSO user and redirect to login page
   * Uses the same mechanism as the "Cancel" button on the linking confirmation page
   */
  _logoutAndRedirectToLogin = () => {
    console.log('Logging out SSO user and redirecting to login page...');

    // Clear base auth-related cookies
    Auth0UserSessionHelper.clearAuthCookies();

    // Also clear linking-specific cookies for full cleanup on error
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IDP_REDIRECT_LOGIN);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_IDP_CONTEXT_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_RESTRICTED_ACCOUNT_LINK_KEY);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_SOURCE_CONTEXT_KEY);

    // Set force logout cookie (must be an object with logoutType property)
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_FORCE_LOGOUT,
      value: { logoutType: Auth0Constants.LOGOUT_TYPE_REDIRECT },
      expirySec: 600,
      stringify: true,
    });

    // Redirect to login page with targetClientId for tenant resolution
    // Use base URL without search params, but add back targetClientId
    const baseLoginUrl = Auth0UtilsBrowser.locationWithNoSearchParams;
    const loginUrl = this._ssOptions.clientId
      ? `${baseLoginUrl}?targetClientId=${encodeURIComponent(this._ssOptions.clientId)}`
      : baseLoginUrl;
    console.log('Redirecting to login page after logout:', loginUrl);
    window.location.href = loginUrl;
  };

  /**
   * Check if the IdP user is linked to a ShipStation account.
   * Uses universal detection based on sub claim prefix:
   * - SSO users (not linked): sub = 'google-oauth2|...', 'microsoft|...', etc.
   * - Linked users: sub = 'auth0|...' (authenticate AS the primary SS account)
   *
   * This is cross-tenant compatible (works for ShipStation, Partners, etc.)
   * without relying on tenant-specific claims like seller_id.
   *
   * @param {object} userData - Login data containing user info
   * @returns {boolean} True if user is linked to an SS account
   */
  _isIdpUserLinkedToSsAccount = (userData) => {
    console.log(`_isIdpUserLinkedToSsAccount => ${JSON.stringify(userData)}`);
    const userSub = userData?.user?.sub;
    if (!userSub) return false;

    // Universal detection: When SSO user links to SS account, they authenticate
    // AS the primary (database) user, so their sub changes from 'google-oauth2|...'
    // to 'auth0|...'. This works across all tenant classes.
    const isAuthenticatedAsPrimaryUser = userSub.startsWith(Auth0Constants.AUTH0_ID_PREFIX);
    if (isAuthenticatedAsPrimaryUser) {
      console.log('_isIdpUserLinkedToSsAccount => detected auth0| prefix, user is linked');
      return true;
    }

    // Fallback: Check session helper for seller_id (ShipStation-specific, backwards compat)
    const hasSellerIdInClaims = Auth0UserSessionHelper.userSellerId;
    if (hasSellerIdInClaims) {
      console.log('_isIdpUserLinkedToSsAccount => detected seller_id claim, user is linked');
      return true;
    }

    console.log('_isIdpUserLinkedToSsAccount => user is not linked (SSO-only identity)');
    return false;
  };

  _renderShipStationLinkingConfirmation = async (socialIdPUserData, linkingData) => {
    const { sub } = socialIdPUserData.user;
    const socialProviderId = Auth0IdentityProvider.getIdpIdFromUserId(sub);
    const socialProvider = this.supportedSocialIdps.find(idp => idp.id === socialProviderId);
    const givenName = linkingData.given_name || socialIdPUserData.user.given_name || linkingData.name || socialIdPUserData.user.name || linkingData.nickname || socialIdPUserData.user.nickname || linkingData.email || socialIdPUserData.user.email;
    const picture = linkingData?.picture || socialIdPUserData.user.picture || socialProvider.logoUrl;

    // Determine the final redirect URL after linking completes
    // Priority: appState.redirectOverride > appState.targetUrl > current location
    // The targetUrl should be where the user actually wants to go (e.g., the app UI)
    // NOT the /loginauth callback URL
    console.log('_renderShipStationLinkingConfirmation => _ssOptions:', JSON.stringify(this._ssOptions));
    console.log('_renderShipStationLinkingConfirmation => _ssOptions.targetUrl:', this._ssOptions?.targetUrl);
    console.log('_renderShipStationLinkingConfirmation => _ssOptions.appLoginUrl:', this._ssOptions?.appLoginUrl);
    console.log('_renderShipStationLinkingConfirmation => _ssOptions.redirectAuth0Uri:', this._ssOptions?.redirectAuth0Uri);
    console.log('_renderShipStationLinkingConfirmation => appState:', JSON.stringify(socialIdPUserData?.appState));
    console.log('_renderShipStationLinkingConfirmation => appState.redirectOverride:', socialIdPUserData.appState?.redirectOverride);
    console.log('_renderShipStationLinkingConfirmation => appState.targetUrl:', socialIdPUserData.appState?.targetUrl);
    console.log('_renderShipStationLinkingConfirmation => window.location:', window.location.href);

    const finalRedirectUrl = socialIdPUserData.appState?.redirectOverride
      || socialIdPUserData.appState?.targetUrl
      || `${window.location.origin}${window.location.pathname}`;

    console.log('_renderShipStationLinkingConfirmation => resolved finalRedirectUrl:', finalRedirectUrl);

    const resolvedAppLoginHandlerUrl = this._ssOptions.appLoginUrl || window.location.href;
    console.log('[appLoginUrl custody] _renderShipStationLinkingConfirmation => ssOptions.appLoginUrl:', this._ssOptions?.appLoginUrl);
    console.log('[appLoginUrl custody] _renderShipStationLinkingConfirmation => window.location.href (fallback):', window.location.href);
    console.log('[appLoginUrl custody] _renderShipStationLinkingConfirmation => resolvedAppLoginHandlerUrl:', resolvedAppLoginHandlerUrl);

    const linkOptions = {
      domain: this._ssOptions.domain,
      tenant: this._ssOptions.tenant,
      clientId: this._ssOptions.clientId,
      connection: this._ssOptions.connection || 'Central-Connection',
      given_name: givenName,
      name: linkingData.name,
      email: linkingData.email,
      picture,
      user_id: sub,
      idp_id: socialProviderId,
      appLoginHandlerUrl: resolvedAppLoginHandlerUrl,
      redirectUrl: finalRedirectUrl,
      message: socialIdPUserData.message,
      messageReplacementArgs: socialIdPUserData.messageReplacementArgs,
      appName: this._whiteLabelHelper.appName,
      whiteLabelOptions: this._whiteLabelOptions,
      captchaOptions: this._captchaOptions,
      tokenStorageConfig: this._ssOptions.tokenStorageConfig,
    };

    console.log(`_renderShipStationLinkingConfirmation => tokenStorageConfig being passed to linking page: ${JSON.stringify(linkOptions.tokenStorageConfig ?? null)}`);
    console.log(`_redirectToShipStationAccountLinkPage idp data => ${JSON.stringify(socialIdPUserData)}`);
    console.log(`_redirectToShipStationAccountLinkPage options => ${JSON.stringify(linkOptions)}`);
    new Auth0LinkingPageHelper(linkOptions).render();
  };

  _getIdpTokenData = async (idpId) => {
    console.log(`_getIdpTokenData => for ${idpId}`);
    const a0Options = {
      domain: this._ssOptions.domain,
      clientId: this._ssOptions.clientId,
      authorizationParams: {
        connection: idpId,
      },
      cacheLocation: 'localstorage',
    };

    try {
      const auth0IdPClient = await this._auth0.createAuth0Client(a0Options);
      // this is currently the last place we'll see tokens for the actual IdP itself (as opposed to SS tokens in the context of an IdP user)
      // Right now, we'll use this spot to fetch and store these tokens for future use in the SS app.
      // Can we refresh these if we need to?
      const idpIdToken = await auth0IdPClient.getIdTokenClaims();
      const idpAccessToken = await auth0IdPClient.getTokenSilently();
      const idpTokens = JSON.stringify({
        // idToken: idpIdToken,
        accessToken: idpAccessToken,
      });

      Auth0CookieHelper.setCookie(Auth0Constants.SECONDARY_TOKENS_KEY, idpTokens, { expiry: Auth0CookieHelper.getExpirationFromDays(1) });
      console.log(`_idpLoginRedirectHandler => Access Tokens and claims => ${idpTokens}`);
      return idpIdToken;
    } catch (e) {
      const idpName = this.socialIdpHelper.getIdpNameById(idpId);
      Auth0UtilsBrowser.setAuth0IsError(`Failed to complete SSO login for ${idpName}. ${e}`);
      throw e;
    }
  };
}

export default Auth0PostLoginHandlerAccountLinking;
